import React, { useMemo } from "react";
import { HistoryDetailsItem } from "@common/types/auditLog";
import { getShortJournalEntryReferenceNumber } from "@common/helpers/accounting";
import { Loader } from "@design-system/Loader/Loader";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { SpreadsheetData } from "@components/spreadsheet/types";
import { DatePicker } from "@design-system/DatePicker/DatePicker";
import { EntitySelect } from "@components/entity/EntitySelect";
import { useCounterpartyNameMap } from "@components/counterparty/useCounterpartyNameMap";
import { useAccounts } from "@hooks/useAccounts";
import { useShowAccountNumbers } from "@hooks/useShowAccountNumbers";
import { useClasses } from "@api/class.api";
import { useEntitiesById } from "@hooks/useEntities";
import { getDiffEntry } from "../../utils/getDiffEntry";
import { JournalEntryLinesTable } from "./JournalEntryLinesTable";
import { HistoryJournalEntrySidebar } from "./HistoryJournalEntrySidebar";
import { buildLinesWithDiffs } from "./buildJournalEntryLines";
import styles from "./HistoryJournalEntryDetails.module.scss";

interface HistoryJournalEntryDetailsProps {
    data: HistoryDetailsItem[];
    footer: React.ReactNode;
    isLoading?: boolean;
}

export const HistoryJournalEntryDetails: React.FC<
    HistoryJournalEntryDetailsProps
> = ({ data, footer, isLoading }) => {
    const log = data[0];

    const { accounts, accountsById } = useAccounts({
        entityId: log?.entityId ?? undefined,
        includeDisabled: true,
    });

    const showAccountNumbers = useShowAccountNumbers();

    const counterpartiesById = useCounterpartyNameMap();

    const { classesMap, classes } = useClasses();
    const hasAnyClass = classes.length > 0;
    const entitiesById = useEntitiesById();
    const entity = log?.entityId ? entitiesById.get(log.entityId) : undefined;

    const classesById = useMemo(() => {
        const map = new Map<string, string>();
        for (const [id, cls] of classesMap.entries()) {
            map.set(id, cls.label);
        }
        return map;
    }, [classesMap]);

    const lines = useMemo<SpreadsheetData>(() => {
        if (!log) {
            return [];
        }
        return buildLinesWithDiffs(log, {
            accountsById,
            showAccountNumbers,
            counterpartiesById,
            classesById,
        });
    }, [
        log,
        accountsById,
        showAccountNumbers,
        counterpartiesById,
        classesById,
    ]);

    const reference = useMemo(() => {
        const refNumber = log?.resource?.referenceNumber;
        if (!refNumber || typeof refNumber !== "string") {
            return undefined;
        }
        return getShortJournalEntryReferenceNumber(refNumber);
    }, [log]);

    const dateValue = useMemo(() => {
        const dateDiff = log ? getDiffEntry(log.diff, "date") : undefined;
        const dateVal = dateDiff?.after?.value ?? log?.resource?.date;
        if (dateVal) {
            return typeof dateVal === "string"
                ? dateVal.slice(0, 10)
                : dateVal.toISOString().slice(0, 10);
        }
        if (log?.createdAt) {
            return String(log.createdAt).slice(0, 10);
        }
        return undefined;
    }, [log]);

    if (isLoading) {
        return (
            <div className={styles.loaderContainer}>
                <Loader />
            </div>
        );
    }

    if (!log) {
        return <EmptyState header="No journal entry data" />;
    }

    return (
        <div className={styles.container}>
            <div className={styles.toolbar}>
                <div className={styles.toolbarLeft}>
                    <DatePicker
                        mode="day"
                        variant="panel-input"
                        className={styles.dateInput}
                        clearable={false}
                        value={dateValue}
                        onValueChange={() => {}}
                        disabled
                        buttonProps={{
                            variant: "primary",
                            size: "md",
                        }}
                    />
                    {entity && (
                        <EntitySelect
                            entities={[entity]}
                            selectedEntity={entity}
                            onChange={() => {}}
                            readOnly
                        />
                    )}
                </div>
            </div>

            <div className={styles.content}>
                <div className={styles.spreadsheetPanel}>
                    <JournalEntryLinesTable
                        lines={lines}
                        accounts={accounts}
                        counterpartiesMap={counterpartiesById}
                        classesMap={classesMap}
                        hasAnyClass={hasAnyClass}
                    />
                </div>

                <div className={styles.sidebarPanel}>
                    <HistoryJournalEntrySidebar
                        reference={reference}
                        diffs={log.diff}
                        resource={log.resource}
                        entityId={log.entityId ?? undefined}
                    />
                </div>
            </div>

            {footer}
        </div>
    );
};
